"use client";

import { useMemo, useState } from "react";
import {
  BdCalendar,
  BdCalendarDate,
  BdConfettiMinimalistic,
} from "solar-icon-react/bd";
import {
  isValidJalaaliDate,
  jalaaliMonthLength,
  toGregorian,
  toJalaali,
} from "jalaali-js";
import { cn } from "@/lib";

type CalendarMode = "jalali" | "gregorian";

type AgeResult = {
  exact: string;
  totalMonths: number;
  totalWeeks: number;
  totalDays: number;
  totalHours: number;
  jalaliBirthDate: string;
  gregorianBirthDate: string;
  birthWeekday: string;
  nextBirthday: string;
  daysToNextBirthday: number;
  legalAgeDate: string;
  nextCakeCandles: number;
  zodiac: string;
};

const jalaliMonths = [
  "فروردین",
  "اردیبهشت",
  "خرداد",
  "تیر",
  "مرداد",
  "شهریور",
  "مهر",
  "آبان",
  "آذر",
  "دی",
  "بهمن",
  "اسفند",
];

const gregorianMonths = [
  "ژانویه",
  "فوریه",
  "مارس",
  "آپریل",
  "می",
  "ژوئن",
  "ژوئیه",
  "آگوست",
  "سپتامبر",
  "اکتبر",
  "نوامبر",
  "دسامبر",
];

const weekDays = [
  "یکشنبه",
  "دوشنبه",
  "سه‌شنبه",
  "چهارشنبه",
  "پنجشنبه",
  "جمعه",
  "شنبه",
];

const zodiacByJalaliMonth = [
  "حمل",
  "ثور",
  "جوزا",
  "سرطان",
  "اسد",
  "سنبله",
  "میزان",
  "عقرب",
  "قوس",
  "جدی",
  "دلو",
  "حوت",
];

const now = () => new Date();

const toEnglishDigits = (value: string) =>
  value
    .replace(/[۰-۹]/g, (digit) => String("۰۱۲۳۴۵۶۷۸۹".indexOf(digit)))
    .replace(/[٠-٩]/g, (digit) => String("٠١٢٣٤٥٦٧٨٩".indexOf(digit)));

const formatNumber = (value: number) =>
  Math.floor(value).toLocaleString("fa-IR");

const formatDateNumber = (value: number) =>
  String(value).replace(/\d/g, (digit) => "۰۱۲۳۴۵۶۷۸۹"[Number(digit)]);

const formatJalaliDate = (jy: number, jm: number, jd: number) =>
  `${formatDateNumber(jd)} ${jalaliMonths[jm - 1]} ${formatDateNumber(jy)}`;

const formatGregorianDate = (date: Date) =>
  `${formatDateNumber(date.getDate())} ${gregorianMonths[date.getMonth()]} ${formatDateNumber(date.getFullYear())}`;

const formatDateAsJalali = (date: Date) => {
  const jalali = toJalaali(
    date.getFullYear(),
    date.getMonth() + 1,
    date.getDate(),
  );

  return formatJalaliDate(jalali.jy, jalali.jm, jalali.jd);
};

const getDaysInGregorianMonth = (year: number, month: number) =>
  new Date(year, month, 0).getDate();

const diffExactAge = (birthDate: Date, targetDate: Date) => {
  let years = targetDate.getFullYear() - birthDate.getFullYear();
  let months = targetDate.getMonth() - birthDate.getMonth();
  let days = targetDate.getDate() - birthDate.getDate();

  if (days < 0) {
    months -= 1;
    days += getDaysInGregorianMonth(
      targetDate.getFullYear(),
      targetDate.getMonth(),
    );
  }

  if (months < 0) {
    years -= 1;
    months += 12;
  }

  return { years, months, days };
};

const getNextBirthday = (birthDate: Date, targetDate: Date) => {
  const candidate = new Date(
    targetDate.getFullYear(),
    birthDate.getMonth(),
    birthDate.getDate(),
  );

  if (candidate < new Date(targetDate.toDateString())) {
    candidate.setFullYear(candidate.getFullYear() + 1);
  }

  return candidate;
};

const calculateAge = ({
  mode,
  year,
  month,
  day,
}: {
  mode: CalendarMode;
  year: number;
  month: number;
  day: number;
}): AgeResult | null => {
  if (!year || !month || !day) return null;

  let birthDate: Date;

  if (mode === "jalali") {
    if (!isValidJalaaliDate(year, month, day)) return null;
    const gregorian = toGregorian(year, month, day);
    birthDate = new Date(gregorian.gy, gregorian.gm - 1, gregorian.gd);
  } else {
    const daysInMonth = getDaysInGregorianMonth(year, month);
    if (month < 1 || month > 12 || day < 1 || day > daysInMonth) return null;
    birthDate = new Date(year, month - 1, day);
  }

  const today = now();
  if (birthDate > today) return null;

  const exactAge = diffExactAge(birthDate, today);
  const totalDays = Math.floor(
    (today.getTime() - birthDate.getTime()) / (1000 * 60 * 60 * 24),
  );
  const totalMonths = exactAge.years * 12 + exactAge.months;
  const totalWeeks = Math.floor(totalDays / 7);
  const totalHours = totalDays * 24;
  const jalali = toJalaali(
    birthDate.getFullYear(),
    birthDate.getMonth() + 1,
    birthDate.getDate(),
  );
  const nextBirthday = getNextBirthday(birthDate, today);
  const daysToNextBirthday = Math.max(
    0,
    Math.ceil(
      (nextBirthday.getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
    ),
  );
  const legalAgeDate = new Date(birthDate);
  legalAgeDate.setFullYear(legalAgeDate.getFullYear() + 18);

  return {
    exact: `${exactAge.years.toLocaleString("fa-IR")} سال و ${exactAge.months.toLocaleString("fa-IR")} ماه و ${exactAge.days.toLocaleString("fa-IR")} روز`,
    totalMonths,
    totalWeeks,
    totalDays,
    totalHours,
    jalaliBirthDate: formatJalaliDate(jalali.jy, jalali.jm, jalali.jd),
    gregorianBirthDate: formatGregorianDate(birthDate),
    birthWeekday: weekDays[birthDate.getDay()],
    nextBirthday: formatDateAsJalali(nextBirthday),
    daysToNextBirthday,
    legalAgeDate: formatDateAsJalali(legalAgeDate),
    nextCakeCandles: exactAge.years + 1,
    zodiac: zodiacByJalaliMonth[jalali.jm - 1],
  };
};

const AgeCalculator = () => {
  const [mode, setMode] = useState<CalendarMode>("jalali");
  const currentJalaliYear = toJalaali(
    now().getFullYear(),
    now().getMonth() + 1,
    now().getDate(),
  ).jy;
  const [year, setYear] = useState("");
  const [month, setMonth] = useState("");
  const [day, setDay] = useState("");
  const [hasCalculated, setHasCalculated] = useState(false);

  const parsedYear = Number(toEnglishDigits(year));
  const parsedMonth = Number(toEnglishDigits(month));
  const parsedDay = Number(toEnglishDigits(day));

  const result = useMemo(() => {
    if (!hasCalculated) return null;
    return calculateAge({
      mode,
      year: parsedYear,
      month: parsedMonth,
      day: parsedDay,
    });
  }, [hasCalculated, mode, parsedDay, parsedMonth, parsedYear]);

  const monthOptions =
    mode === "jalali"
      ? jalaliMonths.map((label, index) => ({ label, value: index + 1 }))
      : gregorianMonths.map((label, index) => ({ label, value: index + 1 }));

  const maxDay =
    mode === "jalali" && parsedYear && parsedMonth
      ? jalaaliMonthLength(parsedYear, parsedMonth)
      : mode === "gregorian" && parsedYear && parsedMonth
        ? getDaysInGregorianMonth(parsedYear, parsedMonth)
        : 31;

  const yearPlaceholder = mode === "jalali" ? "مثلا 1375" : "مثلا 1996";

  return (
    <div className="relative w-full rounded-2xl bg-linear-to-b from-yellow-500 to-transparent p-[2px] shadow-[0_24px_70px_rgba(15,23,42,0.08)] sm:rounded-3xl">
      <div className="grid size-full items-stretch gap-5 rounded-[18px] bg-white p-3 sm:gap-6 sm:rounded-[22px] sm:p-4 lg:grid-cols-2 lg:p-6">
        <section className="order-1 flex h-full flex-col">
          <div className="border-primary-main/15 bg-primary-main/[0.03] mb-4 flex items-center gap-3 rounded-2xl border p-3 sm:p-4">
            <span className="bg-primary-main/10 text-primary-main flex size-10 shrink-0 items-center justify-center rounded-2xl sm:size-12">
              <BdCalendarDate size={30} />
            </span>
            <div className="min-w-0">
              <h2 className="text-base leading-8 font-bold text-slate-900 sm:text-xl">
                محاسبه سن آنلاین
              </h2>
              <p className="mt-1 text-sm text-slate-500">
                رایگان · آنلاین · بدون ثبت‌نام
              </p>
            </div>
          </div>

          <div className="border-primary-main/15 mb-4 rounded-2xl border p-4">
            <h3 className="font-bold text-slate-800">تقویم تاریخ تولد</h3>
            <p className="mt-2 text-sm leading-7 text-slate-500">
              تاریخ تولد را وارد کنید تا سن دقیق به سال، ماه، روز و چند شاخص
              کاربردی دیگر نمایش داده شود.
            </p>
          </div>

          <div className="mb-4 grid grid-cols-2 gap-2">
            {[
              { key: "jalali", label: "شمسی" },
              { key: "gregorian", label: "میلادی" },
            ].map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => {
                  setMode(item.key as CalendarMode);
                  setHasCalculated(false);
                }}
                className={cn(
                  "border-primary-main/15 hover:border-primary-main/35 min-h-12 rounded-xl border bg-white px-4 py-3 text-sm font-bold text-slate-700 transition",
                  mode === item.key &&
                    "bg-primary-main text-white shadow-[0_10px_24px_rgba(65,56,168,0.25)]",
                )}
              >
                {item.label}
              </button>
            ))}
          </div>

          <div className="mb-4 flex flex-wrap justify-center gap-2 sm:justify-start">
            {[
              {
                label: "متولد ۱۳۷۰",
                y: "1370",
                m: "7",
                d: "1",
                mode: "jalali",
              },
              {
                label: "رسیدن به ۱۸ سالگی",
                y: String(currentJalaliYear - 18),
                m: "1",
                d: "1",
                mode: "jalali",
              },
              {
                label: "سن کودک",
                y: String(currentJalaliYear - 7),
                m: "6",
                d: "15",
                mode: "jalali",
              },
            ].map((preset) => (
              <button
                key={preset.label}
                type="button"
                onClick={() => {
                  setMode(preset.mode as CalendarMode);
                  setYear(preset.y);
                  setMonth(preset.m);
                  setDay(preset.d);
                  setHasCalculated(true);
                }}
                className="border-primary-main/15 text-primary-main hover:bg-primary-main/[0.04] rounded-full border px-3 py-1 text-xs font-bold transition"
              >
                {preset.label}
              </button>
            ))}
          </div>

          <div className="grid gap-4 min-[520px]:grid-cols-3">
            <InputBlock
              label="سال"
              value={year}
              onChange={(value) => {
                setYear(value);
                setHasCalculated(false);
              }}
              placeholder={yearPlaceholder}
            />
            <SelectBlock
              label="ماه"
              value={month}
              options={monthOptions}
              onChange={(value) => {
                setMonth(value);
                setHasCalculated(false);
              }}
            />
            <SelectBlock
              label="روز"
              value={day}
              options={Array.from({ length: maxDay }, (_, index) => ({
                label: String(index + 1),
                value: index + 1,
              }))}
              onChange={(value) => {
                setDay(value);
                setHasCalculated(false);
              }}
            />
          </div>

          <button
            type="button"
            onClick={() => setHasCalculated(true)}
            className="bg-primary-main mt-5 h-14 rounded-2xl text-base font-bold text-white shadow-[0_12px_28px_rgba(65,56,168,0.25)] transition hover:brightness-105"
          >
            محاسبه سن
          </button>

          <div className="bg-primary-main/[0.03] mt-5 rounded-2xl p-3 text-sm leading-7 text-slate-600 sm:mt-auto sm:p-4">
            محاسبه سن بر اساس تاریخ امروز دستگاه شما انجام می‌شود. معادل‌های
            تقویمی برای کاربرد عمومی نمایش داده شده‌اند و برای امور رسمی بهتر
            است با مدارک هویتی تطبیق داده شوند.
          </div>
        </section>

        <section className="border-primary-main/20 bg-primary-main/[0.02] order-2 flex h-full min-w-0 flex-col rounded-2xl border border-dashed p-3 sm:p-4">
          <div className="mb-5 flex items-start gap-3">
            <span className="bg-primary-main/10 text-primary-main flex size-10 shrink-0 items-center justify-center rounded-2xl">
              <BdConfettiMinimalistic size={26} />
            </span>
            <div>
              <h3 className="text-lg font-bold text-slate-800">
                سن دقیق شما امروز
              </h3>
              <p className="mt-2 text-sm leading-7 text-slate-600">
                بعد از محاسبه، سن دقیق، تعداد روزهای عمر، تولد بعدی و تاریخ ۱۸
                سالگی نمایش داده می‌شود.
              </p>
            </div>
          </div>

          {hasCalculated && !result ? (
            <div className="mb-4 rounded-2xl border border-amber-100 bg-amber-50 p-4 text-sm leading-7 text-amber-800">
              تاریخ واردشده معتبر نیست یا در آینده قرار دارد. لطفاً روز، ماه و
              سال تولد را بررسی کنید.
            </div>
          ) : null}

          <div className="grid grid-cols-1 gap-3 min-[430px]:grid-cols-2">
            <SummaryCard
              label="سن دقیق"
              text={result?.exact || "-"}
              className="min-[430px]:col-span-2"
            />
            <SummaryCard
              label="سن به ماه"
              text={result ? formatNumber(result.totalMonths) : "-"}
            />
            <SummaryCard
              label="سن به هفته"
              text={result ? formatNumber(result.totalWeeks) : "-"}
            />
            <SummaryCard
              label="سن به روز"
              text={result ? formatNumber(result.totalDays) : "-"}
            />
            <SummaryCard
              label="سن به ساعت"
              text={result ? formatNumber(result.totalHours) : "-"}
            />
            <SummaryCard
              label="تاریخ تولد شمسی"
              text={result?.jalaliBirthDate || "-"}
            />
            <SummaryCard
              label="تاریخ تولد میلادی"
              text={result?.gregorianBirthDate || "-"}
            />
            <SummaryCard
              label="روز هفته تولد"
              text={result?.birthWeekday || "-"}
            />
            <SummaryCard
              label="شمارش تا تولد بعدی"
              text={
                result ? `${formatNumber(result.daysToNextBirthday)} روز` : "-"
              }
            />
            <SummaryCard
              label="شمع تولد بعدی"
              text={result ? formatNumber(result.nextCakeCandles) : "-"}
            />
            <SummaryCard label="برج فلکی" text={result?.zodiac || "-"} />
          </div>

          <div className="mt-5 flex-1 rounded-2xl border border-slate-100 bg-white p-4">
            {result ? (
              <div className="space-y-4 text-sm leading-8 text-slate-700">
                <p>
                  سن دقیق شما امروز{" "}
                  <strong className="text-primary-main">{result.exact}</strong>{" "}
                  است.
                </p>
                <p>
                  تولد بعدی شما در تاریخ <strong>{result.nextBirthday}</strong>{" "}
                  است و تا آن حدود{" "}
                  <strong>{formatNumber(result.daysToNextBirthday)} روز</strong>{" "}
                  باقی مانده.
                </p>
                <p>
                  تاریخ رسیدن به ۱۸ سالگی:{" "}
                  <strong>{result.legalAgeDate}</strong>
                </p>
              </div>
            ) : (
              <div className="flex min-h-[220px] items-center justify-center text-center text-sm leading-7 text-slate-500">
                تاریخ تولد را وارد کنید و دکمه محاسبه سن را بزنید تا نتیجه در
                این بخش نمایش داده شود.
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

const InputBlock = ({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}) => (
  <div>
    <label className="mb-2 block text-sm font-bold text-slate-700">
      {label}
    </label>
    <div className="border-primary-main/10 focus-within:border-primary-main/45 h-14 w-full rounded-xl border bg-white px-4 transition">
      <input
        value={value}
        onChange={(event) => onChange(toEnglishDigits(event.target.value))}
        inputMode="numeric"
        placeholder={placeholder}
        className="size-full border-0 bg-transparent text-left text-sm font-bold text-slate-800 outline-none"
        dir="ltr"
      />
    </div>
  </div>
);

const SelectBlock = ({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: { label: string; value: number }[];
  onChange: (value: string) => void;
}) => (
  <div>
    <label className="mb-2 block text-sm font-bold text-slate-700">
      {label}
    </label>
    <select
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className="border-primary-main/10 focus:border-primary-main/45 h-14 w-full rounded-xl border bg-white px-4 text-sm font-bold text-slate-800 outline-none"
    >
      <option value="">انتخاب کنید</option>
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  </div>
);

const SummaryCard = ({
  label,
  text,
  className,
}: {
  label: string;
  text: string;
  className?: string;
}) => (
  <div
    className={cn(
      "border-primary-main/10 rounded-xl border bg-white p-3 text-center",
      className,
    )}
  >
    <p className="text-primary-main text-sm font-bold">{label}</p>
    <p className="mt-2 text-base font-black text-slate-900">{text}</p>
  </div>
);

export default AgeCalculator;
